import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // IMPORTANT: every utility a call site might override (width, max-width, padding,
        // radius) is DELIBERATELY UNPREFIXED. tailwind-merge only dedupes within a variant
        // group, so a `sm:`-prefixed base utility can never be removed by a consumer's
        // unprefixed className -- and it wins the cascade at >=640px because responsive
        // rules ship later in the stylesheet at equal specificity. A `sm:max-w-lg` here
        // silently forced every `<DialogContent className="max-w-4xl">` to 512px on
        // desktop. Do NOT reintroduce a responsive prefix on an overridable property;
        // use a fluid clamp() instead (see padding below).
        "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-[clamp(1rem,3vw,1.5rem)] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg",
        "font-garamond",
        className
      )}
      onPointerDownOutside={(e) => {
        // Prevent closing when clicking on Google Places dropdown
        const target = e.target as HTMLElement;
        if (target.closest('.pac-container')) {
          e.preventDefault();
        }
      }}
      onInteractOutside={(e) => {
        // Prevent closing when interacting with Google Places dropdown
        const target = e.target as HTMLElement;
        if (target.closest('.pac-container')) {
          e.preventDefault();
        }
      }}
      aria-describedby={props["aria-describedby"] || undefined}
      {...props}
    >
      {/* Visually hidden description to satisfy Radix UI accessibility requirement */}
      {!props["aria-describedby"] && (
        <DialogPrimitive.Description className="sr-only">
          Dialog content
        </DialogPrimitive.Description>
      )}
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      "font-proxima",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Same unprefixed rule as DialogContent. `sm:justify-end` shadowed call sites
      // asking for `justify-between`, and `sm:space-x-2` is a different CSS property
      // from `gap-*` so it could never be overridden -- footers passing `gap-2` got
      // 16px of spacing (8px gap + 8px margin), which two call sites had hand-patched
      // with `sm:gap-0`. `justify-end` is inert on the auto-height mobile column.
      "flex flex-col-reverse gap-2 justify-end sm:flex-row",
      "font-proxima-light",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, asChild, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    asChild={asChild}
    // With asChild, Radix's Slot CONCATENATES class strings — it does not run
    // tailwind-merge. Passing our own sizing down would leave the child with
    // BOTH "text-lg" and its own "text-3xl", and Tailwind emits utilities
    // alphabetically, so ".text-lg" lands after ".text-3xl" and silently wins.
    // Measured: a 30px dialog header rendered at 18px. asChild exists precisely
    // to adopt the child's own appearance, so contribute no styling at all.
    className={asChild ? className : cn(
      "text-lg leading-none tracking-tight",
      "font-proxima font-bold",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "text-sm text-muted-foreground",
      "font-garamond",
      className
    )}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
